import { useEffect, useState } from 'react';
import { useAuth, type RoleGatewayState } from '../contexts/AuthContext';
import { useGlossary } from '../contexts/GlossaryContext';
import { calculateGatewayProgress, computeAdhikaraScore, deriveStageKey, getArchetypeWeights, resolvePolicyArchetype, detectArchetypeMismatch } from '../lib/gatewayPolicy';
import { syncClerkRoleMetadata } from '../lib/clerkMetadata';
import { VEDIC_DIMENSIONS } from '../lib/vedicDimensions';
import '../styles/OnboardingFlow.css';

interface OnboardingFlowProps {
  onNavigate: (path: string) => void;
  onNavigateHome: () => void;
}

const ROLE_CARDS = [
  { key: 'shiksharthi', emoji: '🌱', san: 'शिक्षार्थी', desc: 'मैं सीखता, बढ़ता, और क्षमता बनाता हूँ।' },
  { key: 'upadhyaya', emoji: '🪔', san: 'उपाध्याय', desc: 'मैं वह तकनीक सिखाता हूँ जिसे मैंने साधा है।' },
  { key: 'acharya', emoji: '📿', san: 'आचार्य', desc: 'मैं ज्ञान, शील, और मार्गदर्शन देता हूँ।' },
  { key: 'guru', emoji: '✨', san: 'गुरु', desc: 'मैं उपस्थित रहकर गहन प्रबोध देता हूँ।' },
  { key: 'raja', emoji: '🏛️', san: 'राजा', desc: 'मैं परियोजना आरम्भ करता और लोक-हित सँभालता हूँ।' },
] as const;

const ARCHETYPES = [
  { key: 'anveshak', emoji: '🧭', title: 'अन्वेषक', desc: 'पैटर्न और प्रश्नों के माध्यम से समझता है।', policy: 'BRAHMIN' },
  { key: 'setu', emoji: '🌉', title: 'सेतु', desc: 'लोगों और विचारों के बीच सेतु बनाता है।', policy: 'VAISHYA' },
  { key: 'rakshak', emoji: '🛡️', title: 'रक्षक', desc: 'संरचना और सुरक्षा का ध्यान रखता है।', policy: 'KSHATRIYA' },
  { key: 'nirmata', emoji: '🏗️', title: 'निर्माता', desc: 'विचार को ठोस रूप देता है।', policy: 'SHUDRA' },
] as const;

const ACHARYA_CRITERIA = [
  { key: 'shravana', label: 'श्रवण', desc: 'मैं पहले सुनता हूँ।' },
  { key: 'smarana', label: 'स्मरण', desc: 'मैं सुनी हुई बात को धारण करता हूँ।' },
  { key: 'samvada', label: 'संवाद', desc: 'मैं स्पष्ट और सद्भावपूर्ण उत्तर देता हूँ।' },
  { key: 'vinaya', label: 'विनय', desc: 'मैं विनम्र बना रहता हूँ।' },
  { key: 'seva', label: 'सेवा', desc: 'मैं सीखने वाले की सेवा करता हूँ।' },
  { key: 'niyama', label: 'नियम', desc: 'मैं प्रतिबद्धता निभाता हूँ।' },
  { key: 'dambhahin', label: 'दम्भहीन', desc: 'समुदाय-प्रमाणित।' },
] as const;

const RAJA_CRITERIA = [
  { key: 'dana', label: 'दान' },
  { key: 'vrata', label: 'व्रत' },
  { key: 'brahmacharya', label: 'ब्रह्मचर्य' },
  { key: 'brahma_dharanam', label: 'ब्रह्म-धारणम्' },
  { key: 'dama', label: 'दम' },
  { key: 'prashantata', label: 'प्रशान्तता' },
  { key: 'bhuta_anukampana', label: 'भूतानुकम्पना' },
  { key: 'anrishansya', label: 'अऋशंस्य' },
  { key: 'parasva_adana_varjanam', label: 'परस्वादानवर्जनम्' },
  { key: 'vyalika_karanam_tyaga', label: 'व्यालिककरणत्यागः' },
  { key: 'matapitru_shushrusha', label: 'मातापितृशुश्रूषा' },
  { key: 'devata_atithi_guru_pujanam', label: 'देवताऽतिथिगुरुपूजनम्' },
  { key: 'ghrina', label: 'घृणा' },
  { key: 'nitya_indriya_sanyama', label: 'नित्येन्द्रियसंयमः' },
] as const;

const DOMAIN_PLEDGES = [
  { key: 'learning', label: 'अध्ययन-क्षेत्र' },
  { key: 'service', label: 'सेवा-क्षेत्र' },
  { key: 'finance', label: 'अर्थ-क्षेत्र' },
  { key: 'family', label: 'गृह-क्षेत्र' },
  { key: 'community', label: 'लोक-क्षेत्र' },
] as const;

function tick(value: boolean): string {
  return value ? '✓' : '○';
}

function StepBar({ step, total }: { step: number; total: number }) {
  return (
    <div className="onb-progress-wrap">
      <div className="onb-progress-fill" style={{ width: `${((step + 1) / total) * 100}%` }} />
    </div>
  );
}

export default function OnboardingFlow({ onNavigate, onNavigateHome }: OnboardingFlowProps) {
  const { t } = useGlossary();
  const { user, profile, policy, updateProfile } = useAuth();
  const [step, setStep] = useState(0);
  const [clerkSync, setClerkSync] = useState<'idle' | 'synced'>('idle');
  const [safeguardingNote, setSafeguardingNote] = useState('');

  useEffect(() => {
    if (!profile) return;
    void syncClerkRoleMetadata(profile.roleGateway).then(() => setClerkSync('synced')).catch((error) => {
      console.error('Clerk metadata sync failed:', error);
    });
  }, [profile?.roleGateway]);

  const gateway = profile?.roleGateway;
  const currentRole = gateway?.selectedRole ?? '';
  const selectedArchetype = gateway?.primaryArchetype ?? '';
  const weights = getArchetypeWeights(policy, selectedArchetype);
  const score = gateway ? computeAdhikaraScore(gateway.adhikara, selectedArchetype, policy) : null;
  const stageKey = score === null ? null : deriveStageKey(score, policy);
  const mismatch = gateway && selectedArchetype
    ? detectArchetypeMismatch(resolvePolicyArchetype(selectedArchetype) ?? 'BRAHMIN', {
      hridaya: gateway.adhikara.hridaya,
      vak: gateway.adhikara.vak,
      jnana: gateway.adhikara.jnana,
      karma: gateway.adhikara.karma,
      phala: gateway.adhikara.phala,
    }, policy)
    : null;
  const progress = profile ? calculateGatewayProgress(profile, policy) : null;
  const ageRange = policy?.platform?.ageRange;
  const ageValue = Number.parseInt(profile?.age ?? '', 10);

  useEffect(() => {
    if (!profile) return;
    if (Number.isFinite(ageValue) && ageRange && ageValue < 18) {
      setSafeguardingNote(`Age is used only for safeguarding; under 18 requires guardian-safe routing.`);
    } else {
      setSafeguardingNote('');
    }
  }, [ageRange, ageValue, profile]);

  const updateGateway = (patch: Partial<RoleGatewayState>) => {
    if (!profile) return;
    updateProfile({
      roleGateway: {
        ...profile.roleGateway,
        ...patch,
      },
    });
  };

  const updateNested = <K extends keyof RoleGatewayState, P extends keyof RoleGatewayState[K]>(
    section: K,
    key: P,
    value: RoleGatewayState[K][P],
  ) => {
    if (!profile) return;
    updateGateway({
      [section]: {
        ...(profile.roleGateway[section] as Record<string, unknown>),
        [key]: value,
      } as RoleGatewayState[K],
    });
  };

  const updateChecked = (
    section: 'acharya' | 'raja' | 'domainPledges',
    key: string,
    value: boolean,
  ) => {
    if (!profile) return;
    updateGateway({
      [section]: {
        ...(profile.roleGateway[section] as Record<string, unknown>),
        [key]: value,
      } as RoleGatewayState[typeof section],
    });
  };

  const setRole = (role: RoleGatewayState['selectedRole']) => updateGateway({ selectedRole: role });

  const finishOnboarding = () => {
    if (!profile || !progress?.ready) return;
    const completedProfile = {
      ...profile,
      roleGateway: {
        ...profile.roleGateway,
        completedAt: profile.roleGateway.completedAt || new Date().toISOString(),
      },
    };
    updateProfile(completedProfile);
    onNavigate('/sangati?onboarding=1');
  };

  const setCommunitiesQueue = () => {
    if (!profile) return;
    updateGateway({
      guruAttestationStatus: 'requested',
      acharya: {
        ...profile.roleGateway.acharya,
        dambhahin: false,
      },
    });
  };

  if (!user || !profile) {
    return (
      <div className="onb-shell">
        <div className="onb-screen">
          <div className="onb-bar">
            <span className="onb-dot" />
            <span className="onb-dot onb-dot--amber" />
            <span className="onb-dot onb-dot--green" />
          </div>
          <div className="onb-body">
            <p>{t('profile.empty.signIn')}</p>
            <button className="onb-btn onb-btn--primary" onClick={onNavigateHome}>{t('profile.button.back')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="onb-shell">
      <div className="onb-screen onb-session-strip">
        <span className="onb-session-badge">Onboarding</span>
        <span className="onb-session-copy">Clerk sync: {clerkSync}</span>
        <span className="onb-session-copy">Policy: {policy?.platform?.name ?? 'Raajadharma Gurukul'}</span>
        {progress && <span className="onb-session-copy">Adhikara: {progress.adhikaraScore ?? '—'} · {stageKey ?? 'awaiting'}</span>}
      </div>

      <div className="onb-screen">
        <div className="onb-bar"><span className="onb-dot" /><span className="onb-dot onb-dot--amber" /><span className="onb-dot onb-dot--green" /></div>
        <div className="onb-body">
          <StepBar step={step} total={5} />
          {step === 0 && (
            <>
              <h2 className="onb-title">भूमिका-प्रवेश द्वार</h2>
              <p className="onb-sub">Who are you in this community?</p>
              <div className="onb-role-grid">
                {ROLE_CARDS.map((role) => (
                  <button
                    key={role.key}
                    type="button"
                    className={`onb-role-card ${currentRole === role.key ? 'onb-role-card--active' : ''}`}
                    onClick={() => setRole(role.key)}
                  >
                    <div className="onb-role-emoji">{role.emoji}</div>
                    <div className="onb-role-san">{role.san}</div>
                    <div className="onb-role-desc">{role.desc}</div>
                  </button>
                ))}
              </div>
              <div className="onb-note">All roles are honoured equally. The role only changes the path.</div>
            </>
          )}

          {step === 1 && (
            <>
              <h2 className="onb-title">पहचान और मूल विवरण</h2>
              <p className="onb-sub">Age is used only for safeguarding. It never gates Adhikara.</p>
              <div className="onb-grid-2">
                <label className="onb-field">
                  <span>Preferred name</span>
                  <input value={profile.alias} onChange={(e) => updateProfile({ alias: e.target.value })} />
                </label>
                <label className="onb-field">
                  <span>Age</span>
                  <input value={profile.age} inputMode="numeric" onChange={(e) => updateProfile({ age: e.target.value.replace(/[^0-9]/g, '').slice(0, 3) })} />
                </label>
                <label className="onb-field">
                  <span>City</span>
                  <input value={gateway?.city ?? ''} onChange={(e) => updateGateway({ city: e.target.value })} />
                </label>
                <label className="onb-field">
                  <span>Languages</span>
                  <input value={profile.nativeLanguage} onChange={(e) => updateProfile({ nativeLanguage: e.target.value })} />
                </label>
              </div>
              <div className="onb-note">{safeguardingNote || `Minimum age: ${ageRange?.min ?? 9}+`}</div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="onb-title">स्वभाव-आकृति चयन</h2>
              <p className="onb-sub">Choose a primary and secondary work-nature. The platform uses this as routing, not as judgment.</p>
              <div className="onb-arch-grid">
                {ARCHETYPES.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`onb-arch-card ${selectedArchetype === item.key ? 'onb-arch-card--primary' : gateway?.secondaryArchetype === item.key ? 'onb-arch-card--secondary' : ''}`}
                    onClick={() => updateGateway({ primaryArchetype: item.key })}
                  >
                    <div className="onb-arch-head">
                      <span>{item.emoji}</span>
                      <strong>{item.title}</strong>
                    </div>
                    <p>{item.desc}</p>
                    <small>Maps to policy archetype: {item.policy}</small>
                  </button>
                ))}
              </div>
              <div className="onb-note">
                {selectedArchetype ? `Policy mapping: ${resolvePolicyArchetype(selectedArchetype) ?? '—'}` : 'Select one archetype to see the policy mapping.'}
              </div>
              <label className="onb-field onb-field--inline">
                <span>Secondary swabhava</span>
                <select value={gateway?.secondaryArchetype ?? ''} onChange={(e) => updateGateway({ secondaryArchetype: e.target.value as RoleGatewayState['secondaryArchetype'] })}>
                  <option value="">Select</option>
                  {ARCHETYPES.map((item) => <option key={item.key} value={item.key}>{item.title}</option>)}
                </select>
              </label>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="onb-title">अधिकार-स्वमूल्यांकन</h2>
              <p className="onb-sub">Sub-score weights are taken from platform.config.yaml for your mapped policy archetype.</p>
              <div className="onb-score-grid">
                {(['hridaya', 'vak', 'jnana', 'karma', 'phala'] as const).map((key) => {
                  const weight = weights?.[key] ?? 0;
                  return (
                    <label key={key} className="onb-score-row">
                      <span>{key.toUpperCase()}</span>
                      <input type="range" min={0} max={9} step={1} value={gateway?.adhikara[key] ?? 0} onChange={(e) => updateNested('adhikara', key, Number(e.target.value))} />
                      <strong>{gateway?.adhikara[key] ?? 0}</strong>
                      <em>{Math.round(weight * 100)}%</em>
                    </label>
                  );
                })}
              </div>
              <div className="onb-stage-box">
                <span>Stage</span>
                <strong>{stageKey ? t(`profileGateway.stage.${stageKey}`) : 'Awaiting'}</strong>
              </div>
              {mismatch?.action === 'ROUTE_TO_REASSESSMENT' && (
                <div className="onb-note">
                  {`Swabhava-ja anomaly: ${mismatch.functionalArchetype}.`}
                  <button className="onb-btn onb-btn--ghost" type="button" onClick={() => onNavigate(mismatch.reassessmentPath)}>
                    Route to reassessment
                  </button>
                </div>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <h2 className="onb-title">
                {currentRole === 'raja' ? 'राजा-स्वीकृति' : currentRole === 'acharya' || currentRole === 'guru' ? 'आचार्य-स्वीकृति' : 'सार-संयोजन'}
              </h2>
              <p className="onb-sub">
                {currentRole === 'raja'
                  ? 'All fourteen Raja criteria and the domain-health pledge are required.'
                  : currentRole === 'acharya' || currentRole === 'guru'
                    ? 'All seven Acharya criteria are binary. Dambhahin routes to the Guru witness queue.'
                    : 'No role-specific assertion is required for this path. Continue to Sangati.'}
              </p>

              {(currentRole === 'acharya' || currentRole === 'guru') && (
                <div className="onb-criteria-list">
                  {ACHARYA_CRITERIA.map((item) => (
                    <div key={item.key} className={`onb-criterion ${gateway?.acharya[item.key as keyof RoleGatewayState['acharya']] ? 'onb-criterion--on' : ''}`}>
                      <button type="button" className="onb-criterion-check" onClick={() => updateChecked('acharya', item.key, !gateway?.acharya[item.key as keyof RoleGatewayState['acharya']])} disabled={item.key === 'dambhahin'}>
                        {tick(!!gateway?.acharya[item.key as keyof RoleGatewayState['acharya']])}
                      </button>
                      <div className="onb-criterion-copy">
                        <strong>{item.label}</strong>
                        <span>{item.desc}</span>
                      </div>
                      {item.key === 'dambhahin' ? (
                        <button type="button" className="onb-chip onb-chip--warn" onClick={setCommunitiesQueue}>Request Guru witness</button>
                      ) : (
                        <span className="onb-chip">Self-assert</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {currentRole === 'raja' && (
                <>
                  <div className="onb-criteria-list onb-criteria-list--two">
                    {RAJA_CRITERIA.map((item) => (
                      <label key={item.key} className={`onb-criterion ${gateway?.raja[item.key as keyof RoleGatewayState['raja']] ? 'onb-criterion--on' : ''}`}>
                        <input type="checkbox" checked={!!gateway?.raja[item.key as keyof RoleGatewayState['raja']]} onChange={(e) => updateChecked('raja', item.key, e.target.checked)} />
                        <span>{item.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="onb-pledge">
                    <h3>Domain health pledge</h3>
                    <div className="onb-criteria-list">
                      {DOMAIN_PLEDGES.map((item) => (
                        <label key={item.key} className={`onb-criterion ${gateway?.domainPledges[item.key] ? 'onb-criterion--on' : ''}`}>
                          <input type="checkbox" checked={!!gateway?.domainPledges[item.key]} onChange={(e) => updateChecked('domainPledges', item.key, e.target.checked)} />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}

              <div className="onb-note">{progress?.ready ? 'Ready for Sangati dashboard.' : 'Complete all required boxes to continue.'}</div>
            </>
          )}

          <div className="onb-actions">
            <button className="onb-btn onb-btn--ghost" onClick={() => setStep((value) => Math.max(0, value - 1))} disabled={step === 0}>Back</button>
            {step < 4 ? (
              <button className="onb-btn onb-btn--primary" onClick={() => setStep((value) => Math.min(4, value + 1))}>Continue</button>
            ) : (
              <button className="onb-btn onb-btn--primary" onClick={finishOnboarding} disabled={!progress?.ready}>Enter Sangati dashboard</button>
            )}
          </div>
        </div>
      </div>

      <div className="onb-screen onb-dim-strip">
        {VEDIC_DIMENSIONS.map((dimension) => (
          <button key={dimension.id} type="button" className="onb-dim-chip" onClick={() => onNavigate('/sangati')}>
            <span>{dimension.icon}</span>
            <span>{dimension.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
